"use client"

import React, { useEffect, useState, useRef } from "react"
import { motion } from "framer-motion"
import Link from "next/link"
import Image from "next/image"
import { LucideIcon, Menu, ChevronDown, ChevronRight, Globe, Sparkles } from "lucide-react"
import { useTheme } from "next-themes"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/shared/theme-toggle"
import { usePathname } from "next/navigation"
import { Skeleton } from "@/components/ui/skeleton"
import { LOGO } from "@/constants/images"
import { MobileSidePanel } from "./mobile-side-panel"

interface NavItem {
  name: string
  url: string
  icon?: LucideIcon
  children?: NavItem[]
  isMegaMenu?: boolean
  flagUrl?: string
}

interface NavBarProps {
  items: NavItem[]
  className?: string
}

export function NavBar({ items, className }: NavBarProps) {
  const pathname = usePathname()
  const [activeTab, setActiveTab] = useState("")
  const [isMobile, setIsMobile] = useState(false)
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false)
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [logoLoaded, setLogoLoaded] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  // Ref for the mobile header bar to calculate its bottom position
  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const [mobilePanelOffset, setMobilePanelOffset] = useState(0);

  // State for Mega Menu
  const [openMegaMenu, setOpenMegaMenu] = useState(false); // For Destinations
  const [megaMenuSelectedCountry, setMegaMenuSelectedCountry] = useState<NavItem | null>(null);
  const [megaMenuSelectedSection, setMegaMenuSelectedSection] = useState<NavItem | null>(null);
  const [showCountryScrollIndicator, setShowCountryScrollIndicator] = useState(false);
  const [showSectionScrollIndicator, setShowSectionScrollIndicator] = useState(false);
  const [showSubPageScrollIndicator, setShowSubPageScrollIndicator] = useState(false);

  const countryListRef = useRef<HTMLDivElement>(null);
  const sectionListRef = useRef<HTMLDivElement>(null);
  const subPageListRef = useRef<HTMLDivElement>(null);

  // Refs for dynamic mega menu positioning
  const navbarPillRef = useRef<HTMLDivElement>(null);
  const studyGuideTriggerRef = useRef<HTMLDivElement>(null);
  const [megaMenuStyles, setMegaMenuStyles] = useState({});

  // Ref and delay for mega menu close timeout
  const closeMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MENU_CLOSE_DELAY = 100; // milliseconds

  // Helper function for scrollability check
  const checkGenericScrollability = (
    ref: React.RefObject<HTMLDivElement | null>,
    setter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (ref.current) {
      const { scrollHeight, clientHeight, scrollTop } = ref.current;
      const isOverflowing = scrollHeight > clientHeight;
      // Consider a small tolerance for floating point inaccuracies
      const isScrolledToBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 2;
      setter(isOverflowing && !isScrolledToBottom);
    } else {
      setter(false); // If ref is null, not scrollable
    }
  };

  // Generalized useEffect for managing scroll indicators for a column
  const useScrollIndicator = (
    listRef: React.RefObject<HTMLDivElement | null>,
    setIndicatorVisibility: React.Dispatch<React.SetStateAction<boolean>>,
    isActive: boolean, // e.g., openMegaMenu
    contentDependencies: any[] // e.g., [megaMenuSelectedCountry] for column 2
  ) => {
    useEffect(() => {
      const check = () => checkGenericScrollability(listRef, setIndicatorVisibility);

      if (isActive) {
        // Initial check after a brief delay for DOM to update
        const initialCheckTimer = setTimeout(check, 50); 

        const currentRef = listRef.current;
        currentRef?.addEventListener('scroll', check);

        // Observe changes in childList or subtree that affect scrollHeight
        const mutationObserver = new MutationObserver(check);
        if (currentRef) {
          mutationObserver.observe(currentRef, { childList: true, subtree: true });
        }

        return () => {
          clearTimeout(initialCheckTimer);
          currentRef?.removeEventListener('scroll', check);
          mutationObserver.disconnect();
        };
      } else {
        setIndicatorVisibility(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, listRef, setIndicatorVisibility, ...contentDependencies]);
  };

  // Apply the hook for each column
  useScrollIndicator(countryListRef, setShowCountryScrollIndicator, openMegaMenu, [items, megaMenuSelectedCountry]);
  useScrollIndicator(sectionListRef, setShowSectionScrollIndicator, openMegaMenu && !!megaMenuSelectedCountry?.children?.length, [megaMenuSelectedCountry]);
  useScrollIndicator(subPageListRef, setShowSubPageScrollIndicator, openMegaMenu && !!megaMenuSelectedSection?.children?.length, [megaMenuSelectedSection]);

  // --- START: Generate NavItems based on destinationmenu.md ---
  const generateCountryNavItems = (countryName: string, countrySlug: string): NavItem[] => {
    const sections: NavItem[] = [
      // 1. Explore [Country]
      {
        name: `Explore ${countryName}`,
        url: `/${countrySlug}/explore`,
        children: [
          { name: "Overview", url: `/${countrySlug}/explore` },
          { name: `Why ${countryName}?`, url: `/${countrySlug}/explore/why-${countrySlug}` },
          { name: "Uni & Courses", url: `/${countrySlug}/explore/university-courses` },
          { name: "Education System", url: `/${countrySlug}/explore/education-system` },
          { name: "Intakes & Timelines", url: `/${countrySlug}/explore/intakes-timelines` },
        ],
      },
      // 2. Admissions
      {
        name: "Admissions",
        url: `/${countrySlug}/admissions`,
        children: [
          { name: "Overview", url: `/${countrySlug}/admissions` },
          { name: "Eligibility", url: `/${countrySlug}/admissions/eligibility` },
          { name: "Documents", url: `/${countrySlug}/admissions/documents` },
          { name: "Language Tests", url: `/${countrySlug}/admissions/language-tests` },
          { name: "SOP/LORs", url: `/${countrySlug}/admissions/sop-lors` },
          { name: "Application Deadlines", url: `/${countrySlug}/admissions/application-deadlines` },
          { name: "How to Apply", url: `/${countrySlug}/admissions/how-to-apply` },
        ],
      },
      // 3. Finance
      {
        name: "Finance",
        url: `/${countrySlug}/finance`,
        children: [
          { name: "Overview", url: `/${countrySlug}/finance` },
          { name: "Tuition Fees", url: `/${countrySlug}/finance/tuition-fees` },
          { name: "Living Costs", url: `/${countrySlug}/finance/living-costs` },
          { name: "Scholarships", url: `/${countrySlug}/finance/scholarships` },
          { name: "Proof of Funds", url: `/${countrySlug}/finance/financial-proof` },
          { name: "Student Jobs", url: `/${countrySlug}/finance/student-jobs` },
        ],
      },
      // 4. Pre-Departure
      {
        name: "Pre-Departure",
        url: `/${countrySlug}/pre-departure`,
        children: [
          { name: "Overview", url: `/${countrySlug}/pre-departure` },
          { name: "Visa Requirements", url: `/${countrySlug}/pre-departure/visa-requirements` },
          { name: "Visa Process", url: `/${countrySlug}/pre-departure/visa-process` },
          { name: "Insurance", url: `/${countrySlug}/pre-departure/insurance` },
          { name: "Accommodation", url: `/${countrySlug}/pre-departure/accommodation` },
          { name: "Final Checklist", url: `/${countrySlug}/pre-departure/checklist` },
        ],
      },
      // 5. Post-Arrival
      {
        name: "Post-Arrival",
        url: `/${countrySlug}/post-arrival`,
        children: [
          { name: "Overview", url: `/${countrySlug}/post-arrival` },
          { name: "Arrival Plan", url: `/${countrySlug}/post-arrival/arrival-plan` },
          { name: "University Enrollment", url: `/${countrySlug}/post-arrival/university-enrollment` },
          { name: "City Registration", url: `/${countrySlug}/post-arrival/city-registration` },
          { name: "Residence Permit", url: `/${countrySlug}/post-arrival/residence-permit` },
          { name: "Banking/SIM", url: `/${countrySlug}/post-arrival/banking-sim` },
          { name: "Healthcare", url: `/${countrySlug}/post-arrival/healthcare` },
          { name: "Student Life", url: `/${countrySlug}/post-arrival/student-life` },
        ],
      },
    ];
    return sections;
  };

  // Define all countries based on provided data, Poland first, then by general popularity
  const allCountriesRawBase = [
    { name: "Poland", slug: "poland", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/poland.svg" },
    { name: "United States", slug: "usa", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/usa.svg" },
    { name: "United Kingdom", slug: "uk", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/uk.svg" },
    { name: "Canada", slug: "canada", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/canada.svg" },
    { name: "Australia", slug: "australia", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/australia.svg" },
    { name: "Germany", slug: "germany", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/germany.svg" },
    { name: "France", slug: "france", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/france.svg" },
    { name: "Netherlands", slug: "netherlands", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/netherlands.svg" },
    { name: "Ireland", slug: "ireland", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/ireland.svg" },
    { name: "Japan", slug: "japan", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/japan.svg" },
    { name: "Singapore", slug: "singapore", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/singapore.svg" },
    { name: "Italy", slug: "italy", flagUrl: "https://storage.googleapis.com/qogent-images/images/flags/italy.svg" },
  ];

  const allCountriesRaw = allCountriesRawBase.map(country => ({
    ...country,
    children: generateCountryNavItems(country.name, country.slug),
  }));
  // --- END: Generate NavItems ---

  // Define all countries based on provided data, Poland first, then by general popularity
  const countryNavItems: NavItem[] = allCountriesRaw.map(country => ({
    name: country.name,
    url: country.slug === "poland" ? "/poland" : `/destinations/${country.slug}`, // This is the main link for the country in Col 1 of Mega Menu
    flagUrl: country.flagUrl,
    children: country.children, // These are the sections for Col 2 (Explore, Admissions, etc.)
  }));
  
  const studyGuideItem: NavItem = {
    name: "StudyGuide",
    url: "#", // Stays as # because it triggers a mega menu
    icon: ChevronDown,
    isMegaMenu: true,
    children: countryNavItems, // Use the structured country list
  };

  // New logic for ordering: Home, then UniSearch, then StudyGuide, then others
  let navigationItems: NavItem[] = [];
  const homeItem = items.find(item => item.name === "Home");
  
  // Define new items - assuming 'items' prop might contain initial versions or we define them fresh
  const uniSearchItem: NavItem = { name: "UniSearch", url: "/universities" }; // Updated URL for UniSearch
  const toolsItem: NavItem = { name: "Tools", url: "/tools" };
  const servicesItem: NavItem = { name: "Services", url: "/services" };
  const aboutItem: NavItem = { name: "About", url: "/about" };

  // Filter out items that are now explicitly managed or removed (like Blog)
  const otherItems = items.filter(item => 
    item.name !== "Home" && 
    item.name !== "Destinations" && // Old name, ensure it's not picked up if present in 'items'
    item.name !== "StudyGuide" && // New name
    item.name !== "UniSearch" &&
    item.name !== "Tools" &&
    item.name !== "Services" &&
    item.name !== "About" &&
    item.name !== "Blog" && // Explicitly remove Blog
    item.name !== "Universities" // Explicitly remove Universities
  );

  if (homeItem) {
    navigationItems.push(homeItem);
  }
  navigationItems.push(uniSearchItem);
  navigationItems.push(studyGuideItem); 
  navigationItems.push(toolsItem);
  navigationItems.push(servicesItem);
  navigationItems.push(aboutItem);
  // Add any other items from the original 'items' prop that weren't caught by the explicit list
  // This preserves extensibility if 'items' prop is used for more than just 'Home' and 'Blog' initially.
  navigationItems.push(...otherItems);

  useEffect(() => {
    setMounted(true)
    
    // Find matching nav item based on the current path for top-level activeTab
    const matchedItem = navigationItems.find(item => {
      if (pathname === item.url) return true
      if (item.url !== '/' && item.url !== '#' && pathname.startsWith(item.url)) return true // Handle non-link parents
      // Special handling for StudyGuide parent
      if (item.name === "StudyGuide" && (pathname.startsWith("/poland") || pathname.startsWith("/destinations/"))) return true; 
      return false
    })
    
    setActiveTab(matchedItem?.name || navigationItems[0]?.name || "")
  }, [pathname, navigationItems])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Track scrolling for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Preload the logo images
  useEffect(() => {
    const preloadPngImage = (pngPath: string) => {
      // pngPath is expected to be like '/qogent_logo.png'
      const image = new window.Image();
      // Only preload the exact PNG path
      image.src = pngPath; 
      image.onload = () => {
        if (pngPath === LOGO.WHITE || pngPath === LOGO.DEFAULT) {
          setLogoLoaded(true);
        }
      };
      image.onerror = () => {
        // console.warn(`Failed to preload Png image ${pngPath}`);
        // Decide if logoLoaded should be set true even on error, 
        // to allow main <Image> tag to attempt loading.
        // For now, if preload fails, skeleton might persist longer.
      };
    };

    if (mounted) {
      // LOGO.WHITE and LOGO.DEFAULT are '/qogent_logo.png'
      // Preload the PNG version directly
      preloadPngImage(LOGO.WHITE); // This is '/qogent_logo.png'
      // If LOGO.DEFAULT is different and also needs preloading:
      if (LOGO.DEFAULT !== LOGO.WHITE) {
         preloadPngImage(LOGO.DEFAULT);
      }
      // Since they are the same in constants, one call is effectively made for '/qogent_logo.png'
    }
  }, [mounted]);

  // Effect to calculate the offset for the mobile side panel
  useEffect(() => {
    const calculateOffset = () => {
      if (mobileHeaderRef.current) {
        const rect = mobileHeaderRef.current.getBoundingClientRect();
        setMobilePanelOffset(rect.bottom);
      }
    };

    if (isMobile && isSidePanelOpen) { // Only calculate when relevant
      calculateOffset(); // Initial calculation
      const observer = new ResizeObserver(calculateOffset);
      if (mobileHeaderRef.current) {
        observer.observe(mobileHeaderRef.current);
      }
      window.addEventListener('resize', calculateOffset);
      window.addEventListener('scroll', calculateOffset, { passive: true });

      return () => {
        if (mobileHeaderRef.current) {
          observer.unobserve(mobileHeaderRef.current);
        }
        observer.disconnect();
        window.removeEventListener('resize', calculateOffset);
        window.removeEventListener('scroll', calculateOffset);
      };
    }
  }, [isMobile, isSidePanelOpen]);

  // Effect to calculate and update mega menu styles
  useEffect(() => {
    const pillEl = navbarPillRef.current;
    const triggerEl = studyGuideTriggerRef.current;

    function updateStyles() {
      if (pillEl && triggerEl) {
        const navbarRect = pillEl.getBoundingClientRect();
        const triggerRect = triggerEl.getBoundingClientRect();

        const left = navbarRect.left - triggerRect.left;
        const width = navbarRect.width;

        setMegaMenuStyles({
          width: `${width}px`,
          left: `${left}px`,
        });
      }
    }

    if (openMegaMenu) {
      updateStyles(); // Initial calculation on open
      window.addEventListener('resize', updateStyles);
      return () => {
        window.removeEventListener('resize', updateStyles);
      };
    }
  }, [openMegaMenu]); // Re-run when openMegaMenu changes

  const NavLinkContent = ({ item, isActive }: { item: NavItem; isActive: boolean }) => {
    // This component renders the inner content of a nav link.
    return (
      <>
        {/* Decorative item.icon rendering removed as per request */}
        <span className="mx-1">{item.name}</span>
        {/* Chevron for mega menu triggers (e.g., Destinations) is kept */}
        {item.isMegaMenu && <ChevronDown className={`h-4 w-4 ml-1 transition-transform duration-200 ${openMegaMenu ? "rotate-180" : ""}`} />}
      </>
    );
  };

  return (
    <>
      {/* Desktop Navigation */}
      <div
        className={cn(
          "relative w-full hidden sm:block transition-all duration-300",
          scrolled ? "py-2" : "py-2",
          className,
        )}
      >
        <div className="flex items-center justify-center gap-3">
          <div ref={navbarPillRef} className="flex items-center gap-3 bg-background/80 border border-border backdrop-blur-lg py-1 px-1 rounded-full shadow-lg">
            {/* Logo */}
            <Link href="/" className="flex items-center px-2">
              {/* Logo placeholder with exact dimensions to prevent layout shifts */}
              <div className="relative h-[42px] w-[145px] flex items-center justify-center">
                {(mounted && logoLoaded) ? (
                  <Image
                    src={theme === 'dark' ? LOGO.WHITE : LOGO.DEFAULT}
                    alt="Qogent Logo"
                    width={145}
                    height={42}
                    className="h-auto w-[145px] max-h-10 object-contain select-none"
                    priority
                    quality={90}
                    loading="eager"
                    fetchPriority="high"
                    unoptimized={true}
                    style={{ 
                      imageRendering: 'crisp-edges'
                    }}
                  />
                ) : (
                  <div className="h-[42px] w-full flex items-center justify-center">
                    <Skeleton className="h-7 w-[120px]" />
                  </div>
                )}
              </div>
            </Link>

            {/* Navigation Items */}
            {navigationItems.map((item) => {
              const isActive = activeTab === item.name

              if (item.isMegaMenu) {
                return (
                  <div
                    key={item.name}
                    className="relative"
                    ref={item.name === "StudyGuide" ? studyGuideTriggerRef : null}
                    onMouseEnter={() => {
                      if (closeMenuTimeoutRef.current) {
                        clearTimeout(closeMenuTimeoutRef.current);
                        closeMenuTimeoutRef.current = null;
                      }
                      setOpenMegaMenu(true);
                      if (item.children && item.children.length > 0) {
                        setMegaMenuSelectedCountry(item.children?.[0] || null);
                        setMegaMenuSelectedSection(item.children?.[0]?.children?.[0] || null);
                      } else {
                        setMegaMenuSelectedCountry(null);
                        setMegaMenuSelectedSection(null);
                      }
                    }}
                    onMouseLeave={() => {
                      closeMenuTimeoutRef.current = setTimeout(() => {
                        setOpenMegaMenu(false);
                      }, MENU_CLOSE_DELAY);
                    }}
                  >
                    <button // Changed to button for accessibility if URL is #
                      onClick={() => setOpenMegaMenu(!openMegaMenu)} // Toggle on click too
                      className={cn(
                        "relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-full transition-colors flex items-center",
                        "text-foreground/80 hover:text-primary",
                        (isActive || openMegaMenu) && "bg-muted text-primary",
                      )}
                    >
                      <NavLinkContent item={item} isActive={isActive || openMegaMenu} />
                    </button>
                    {(isActive || openMegaMenu) && (
                      <motion.div // Lamp for mega menu trigger
                        layoutId="lamp"
                        className="absolute inset-0 w-full bg-primary/5 rounded-full -z-10"
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                      >
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                          <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                          <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-md -top-1" />
                          <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                        </div>
                      </motion.div>
                    )}

                    {/* Mega Menu Panel */}
                    {openMegaMenu && item.children && (
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } }}
                        exit={{ opacity: 0, y: 15, transition: { duration: 0.2, ease: "easeIn" } }}
                        // Solid opaque background, subtle border, removed backdrop-blur
                        className="absolute top-full left-0 mt-[12px] w-[750px] 
                                   bg-background 
                                   border border-border 
                                   rounded-lg shadow-xl p-5 z-50 grid grid-cols-3 gap-x-6 gap-y-4 overflow-hidden"
                        style={megaMenuStyles} // Apply dynamic styles
                        onMouseEnter={() => {
                          if (closeMenuTimeoutRef.current) {
                            clearTimeout(closeMenuTimeoutRef.current);
                            closeMenuTimeoutRef.current = null;
                          }
                          // Ensure menu stays open if mouse re-enters panel quickly
                          if (!openMegaMenu) {
                            setOpenMegaMenu(true);
                          }
                        }} 
                        onMouseLeave={() => {
                          closeMenuTimeoutRef.current = setTimeout(() => {
                            setOpenMegaMenu(false);
                          }, MENU_CLOSE_DELAY);
                        }} 
                      >
                        {/* Localized Glow Element - On top of solid background */}
                        <div
                          className="absolute top-1/2 left-0 -translate-x-1/4 -translate-y-1/2 w-2/3 h-[150%] pointer-events-none -z-[1]"
                          style={{
                            background: "radial-gradient(ellipse at center left, rgba(59, 130, 246, 0.35), transparent 70%)", // Adjusted glow intensity
                            filter: "blur(60px)", 
                          }}
                        />

                        {/* Column 1: Countries - Text and hover/selected states adjusted for solid background */}
                        <motion.div 
                          ref={countryListRef}
                          initial={{ opacity:0, x: -10}} 
                          animate={{ opacity:1, x: 0, transition: {delay: 0.1, duration: 0.3} }}
                          className="flex flex-col space-y-1 pr-3 border-r border-border/60 dark:border-border/40 overflow-y-auto max-h-[300px] relative"
                          style={showCountryScrollIndicator ? {
                            maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                            WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                          } : {}}
                        >
                          {item.children.map((country) => (
                            <Link
                              href={country.url} 
                              key={country.name}
                              className={cn(
                                "flex items-center justify-between p-2.5 text-sm rounded-md transition-all duration-150 ease-in-out",
                                "text-foreground hover:text-primary", // Base text, hover to primary
                                "hover:bg-muted/70 dark:hover:bg-slate-700/50", // Subtle hover bg
                                megaMenuSelectedCountry?.name === country.name ? 
                                  "bg-primary/10 text-primary font-semibold shadow-sm scale-[1.02]" : // Selected state
                                  ""
                              )}
                              onMouseEnter={() => { setMegaMenuSelectedCountry(country); setMegaMenuSelectedSection(country.children?.[0] || null); }}
                              onClick={() => {setOpenMegaMenu(false); setActiveTab(studyGuideItem.name);}}
                            >
                              <div className="flex items-center space-x-2">
                                {country.flagUrl && (
                                  <Image 
                                    src={country.flagUrl} 
                                    alt={`${country.name} flag`} 
                                    width={20} 
                                    height={14} 
                                    className="rounded-sm object-cover"
                                  />
                                )}
                                <span className={cn(megaMenuSelectedCountry?.name === country.name ? "font-bold text-primary" : "font-medium")}>{country.name}</span>
                              </div>
                              {country.children && country.children.length > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            </Link>
                          ))}
                        </motion.div>

                        {/* Column 2: Main Sections */}
                        <motion.div 
                          ref={sectionListRef}
                          initial={{ opacity:0, x: -10}} 
                          animate={{ opacity:1, x: 0, transition: {delay: 0.15, duration: 0.3} }}
                          className="flex flex-col space-y-1 pr-3 pl-3 border-r border-border/60 dark:border-border/40 overflow-y-auto max-h-[300px] relative"
                          style={showSectionScrollIndicator ? {
                            maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                            WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                          } : {}}
                        >
                          {megaMenuSelectedCountry?.children && megaMenuSelectedCountry.children.length > 0 ? (
                            megaMenuSelectedCountry.children.map((section) => (
                              <Link
                                href={section.url} 
                                key={section.name}
                                className={cn(
                                  "flex items-center justify-between p-2.5 text-sm rounded-md transition-all duration-150 ease-in-out",
                                  "text-foreground hover:text-primary",
                                  "hover:bg-muted/70 dark:hover:bg-slate-700/50",
                                  megaMenuSelectedSection?.name === section.name ? 
                                    "bg-primary/10 text-primary font-semibold shadow-sm scale-[1.02]" : 
                                    ""
                                )}
                                onMouseEnter={() => setMegaMenuSelectedSection(section)}
                                onClick={() => {setOpenMegaMenu(false); setActiveTab(studyGuideItem.name);}} // Ensure StudyGuide tab stays active
                              >
                                 <span className={cn(megaMenuSelectedSection?.name === section.name ? "font-semibold text-primary" : "font-medium")}>{section.name}</span>
                                {section.children && section.children.length > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                              </Link>
                            ))
                          ) : (
                            <div className="p-4 text-sm text-muted-foreground flex flex-col items-center justify-center h-full space-y-3">
                              <Sparkles className="w-10 h-10 text-primary/70" />
                              <p className="font-semibold text-base text-foreground/90 text-center">New Adventures Unfolding!</p>
                              <p className="text-xs text-center text-muted-foreground px-2">
                                We&apos;re charting new territories. Check back soon for more amazing destinations!
                              </p>
                            </div>
                          )}
                        </motion.div>

                        {/* Column 3: Sub-pages */}
                        <motion.div 
                          ref={subPageListRef}
                          initial={{ opacity:0, x: -10}} 
                          animate={{ opacity:1, x: 0, transition: {delay: 0.2, duration: 0.3} }}
                          className="flex flex-col space-y-1 overflow-y-auto max-h-[300px] pl-3 relative"
                          style={showSubPageScrollIndicator ? {
                            maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                            WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
                          } : {}}
                        >
                          {megaMenuSelectedSection?.children?.map((subPage) => (
                            <Link
                              href={subPage.url}
                              key={subPage.name}
                              className={cn(
                                "p-2.5 text-sm rounded-md transition-all duration-150 ease-in-out",
                                "text-foreground hover:text-primary",
                                "hover:bg-muted/70 dark:hover:bg-slate-700/50",
                                pathname === subPage.url ? 
                                  "bg-primary/10 text-primary font-semibold shadow-sm" : 
                                  ""
                              )}
                              onClick={() => {setOpenMegaMenu(false); setActiveTab(studyGuideItem.name);}}
                            >
                              {subPage.name}
                            </Link>
                          ))}
                        </motion.div>

                        {/* "Scroll for more" Hint for Column 1 */}
                        {showCountryScrollIndicator && (
                            <div 
                                className="absolute bottom-3 left-[16.66%] -translate-x-1/2 flex justify-center pointer-events-none z-10" // z-10 to be above column content if any overlap briefly
                                style={{ width: "auto" }} // Ensure it sizes to its content
                            >
                                <div className="flex items-center bg-background text-[11px] text-muted-foreground rounded-full px-2.5 py-1 shadow-lg border border-border/30 pointer-events-auto">
                                    <ChevronDown className="h-3 w-3 mr-0.5" />
                                    Scroll for more
                                </div>
                            </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                );
              }

              // Fallback for regular Nav Items (non-mega menu)
              return (
                <div key={item.name} className="relative">
                <Link
                  href={item.url}
                  onClick={() => setActiveTab(item.name)}
                  className={cn(
                        "relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-full transition-colors flex items-center",
                    "text-foreground/80 hover:text-primary",
                    isActive && "bg-muted text-primary",
                  )}
                >
                        <NavLinkContent item={item} isActive={isActive} />
                    </Link>
                    {isActive && ( // Original lamp for non-mega menu items
                    <motion.div
                      layoutId="lamp"
                      className="absolute inset-0 w-full bg-primary/5 rounded-full -z-10"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                        <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                        <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-md -top-1" />
                        <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                      </div>
                    </motion.div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Theme Toggle outside nav container */}
          <div className="flex items-center justify-center bg-background/80 border border-border backdrop-blur-lg w-10 h-10 rounded-full shadow-lg group hover:bg-muted/50 transition-colors">
            {mounted ? (
              <ThemeToggle />
            ) : (
              <div className="w-5 h-5 rounded-full">
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <>
        {/* Mobile Top Logo */}
        <div
          ref={mobileHeaderRef}
          className={cn(
            "relative left-0 w-full z-[52] sm:hidden",
          )}
        >
          <div className="w-full flex justify-between items-center py-4 px-4 bg-white dark:bg-slate-900 border-b border-border">
            {/* Menu button wrapper with fixed width */}
            <div className="w-8">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
                className="w-8 h-8 rounded-full hover:bg-background/10"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Center logo with margin compensation */}
            <div className="flex-1 flex justify-center">
              <div className="relative h-[42px] w-[145px] flex items-center justify-center">
                {(mounted && logoLoaded) ? (
                  <Image
                    src={theme === 'dark' ? LOGO.WHITE : LOGO.DEFAULT}
                    alt="Qogent Logo"
                    width={145}
                    height={42}
                    className="h-auto w-[145px] max-h-10 object-contain select-none"
                    priority
                    quality={90}
                    loading="eager"
                    fetchPriority="high"
                    unoptimized={true}
                    style={{ 
                      imageRendering: 'crisp-edges'
                    }}
                  />
                ) : (
                  <div className="h-[42px] w-full flex items-center justify-center">
                    <Skeleton className="h-7 w-[120px]" />
                  </div>
                )}
              </div>
            </div>

            {/* Theme toggle wrapper with fixed width */}
            <div className="w-8 flex items-center justify-center">
              {mounted ? (
                <ThemeToggle />
              ) : (
                <div className="w-5 h-5 rounded-full">
                  <Skeleton className="h-5 w-5 rounded-full" />
                </div>
              )}
            </div>
          </div>
        </div>
      </>

      {/* Mobile Side Panel */}
      <MobileSidePanel
        items={navigationItems}
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        topOffset={mobilePanelOffset}
      />
    </>
  )
} 